import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { Flex, Text, H1 } from '@revolut/ui-kit'
import type { GeneralState } from 'reducers/generalReducer'
import type { AppState } from 'store'
import type { Dispatch } from 'redux'

import { FetchStates } from 'constants/common'
import type { AccessToken, FundsConfirmationConsent } from 'types/common'
import { fetchPocketsAction } from 'thunks'
import type { PocketsState } from 'reducers/pocketsReducer'
import { CardSlide } from 'components/CardSlide'
import { Loader } from 'components/Loader'
import { AccountDisplay } from 'components/AccountDisplay/AccountDisplay'
import { calculateConsentExpirationDate } from 'helpers/common'
import { ApplicationData } from '../../services/api'

type Props = {
  applicationData: ApplicationData
  general: GeneralState<FundsConfirmationConsent>['data']
  pockets: PocketsState
  fetchPockets: (accessToken: AccessToken) => void
  handleSubmit: () => void
  onBack: () => void
  onClose: () => void
}

const FundsConfirmation: React.FC<Props> = ({
  applicationData,
  handleSubmit,
  onBack,
  onClose,
  general: {
    accessToken,
    consent: { details },
  },
  pockets,
  fetchPockets,
}) => {
  const { pocketId, expiryDate } = details
  useEffect(() => {
    fetchPockets(accessToken)
  }, [accessToken, fetchPockets])

  if (pockets.fetchState === FetchStates.PENDING) {
    return (
      <Flex alignItems='center' minHeight='100vh'>
        <Loader />
      </Flex>
    )
  }
  const accountToConfirm = pockets.data.find((pocket) => pocket.id === pocketId)

  const { expiresAfter, expiresAtString } = calculateConsentExpirationDate(expiryDate)

  return (
    <CardSlide
      onSubmit={handleSubmit}
      header={
        <>
          <H1 mb='1rem'>Sufficient balance check</H1>
          <Text color='grey-50' mt='0.75rem' mb='1rem'>
            <Text color='black' fontWeight={500}>
              {applicationData.name}
            </Text>{' '}
            would like to know if there is enough money on your account without getting a
            certain amount. Permission will expire on {expiresAtString} (in {expiresAfter}{' '}
            days).
          </Text>
        </>
      }
      buttonText='Authorise'
      onBack={onBack}
      onClose={onClose}
    >
      <Flex flexDirection='column'>
        <Text color='black' fontWeight={500}>
          Account to check
        </Text>
        {accountToConfirm && (
          <AccountDisplay
            pb='1rem'
            mt='3rem'
            account={accountToConfirm}
            labelProps={{ fontWeight: 500 }}
            balanceProps={{ color: 'black' }}
          />
        )}
      </Flex>
    </CardSlide>
  )
}

const mapStateToProps = ({ pockets, general }: AppState) => ({
  general: general.data,
  pockets,
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  fetchPockets: (accessToken: AccessToken) => dispatch(fetchPocketsAction(accessToken)),
})

const ConnectedFundsConfirmation = connect(
  mapStateToProps,
  mapDispatchToProps,
)(FundsConfirmation)

export { ConnectedFundsConfirmation as FundsConfirmation }
